import { Position3D } from '../Position';
import { quaternionMultiply } from './quaternionMultiply';
import { Vector3D, Vector } from '../Vector';
import { SquareMatrix3D } from '../Matrix';
export class Orientation3D {
    static identity() {
        return new Orientation3D(1, new Vector3D([0, 0, 0]));
    }
    static fromEuler(e) {
        const xs = e.map((n) => n / 2);
        const [cx, cy, cz] = xs.map(Math.cos);
        const [sx, sy, sz] = xs.map(Math.sin);
        const qw = cx * cy * cz - sx * sy * sz;
        const qx = sx * cy * cz + cx * sy * sz;
        const qy = cx * sy * cz - sx * cy * sz;
        const qz = sx * sy * cz + cx * cy * sz;
        return new Orientation3D(qw, new Vector3D([qx, qy, qz]));
    }
    static fromQuaternion(quad) {
        const [w, x, y, z] = quad;
        return new Orientation3D(w, new Vector3D([x, y, z]));
    }
    /**
     * given a normailsed axis of rotation and
     * an angle in radians, construct an Orientation3D
     */
    static fromAxisAngle(axis, angle) {
        const sin = Math.sin(angle * 0.5);
        const cos = Math.cos(angle * 0.5);
        return new Orientation3D(cos, axis.scale(sin));
    }
    /**
     * the Orientation3D needed to rotate from one 2D direction to another
     */
    static from2DArc(src, dst) {
        const [sx, sz] = src.value();
        const [dx, dz] = dst.value();
        return Orientation3D.from3DArc(new Vector3D([sx, 0, sz]), new Vector3D([dx, 0, dz]));
    }
    static from3DArc(src, dst, fallback = new Vector3D([0, 1, 0])) {
        const avg_mag = src.mag() * dst.mag();
        const dot = src.dot(dst);
        if (Math.abs(avg_mag - dot) < 1e-6) {
            return Orientation3D.identity();
        }
        else if (Math.abs(-avg_mag - dot) < 1e-6) {
            return Orientation3D.fromAxisAngle(fallback, Math.PI);
        }
        else {
            return new Orientation3D(avg_mag + dot, src.cross(dst)).normalise();
        }
    }
    _w;
    _v;
    constructor(w, v) {
        this._w = w;
        this._v = v;
    }
    compose(right) {
        const [nw, nx, ny, nz] = quaternionMultiply(this.quaternion(), right.quaternion());
        return new Orientation3D(nw, new Vector3D([nx, ny, nz]));
    }
    quaternion() {
        return [this._w, ...this._v.value()];
    }
    euler() {
        const qw = this._w;
        const [qx, qy, qz] = this._v.value();
        const s = 2 * (qw * qy + qx * qz);
        const rx = s < 0.9999999
            ? Math.atan2(2 * (qw * qx - qy * qz), 1 - 2 * (qx * qx + qy * qy))
            : Math.atan2(2 * (qy * qz + qw * qx), 1 - 2 * (qx * qx + qz * qz));
        const ry = Math.asin(Math.min(1.0, Math.max(-1.0, s)));
        const rz = s < 0.9999999
            ? Math.atan2(2 * (qw * qz - qx * qy), 1 - 2 * (qy * qy + qz * qz))
            : 0;
        return [rx, ry, rz];
    }
    rotatePoint(pt, origin = new Position3D(0, 0, 0)) {
        return origin.translate(this.rotateVector(pt.subtract(origin)));
    }
    rotateVector(vec) {
        const input = [0, ...vec.value()];
        const conj = [this._w, ...this._v.scale(-1).value()];
        const quaternion = this.quaternion();
        const [, x, y, z] = quaternionMultiply(quaternionMultiply(quaternion, input), conj);
        return new Vector3D([x, y, z]);
    }
    normalise() {
        return Orientation3D.fromQuaternion(new Vector(4, this.quaternion())
            .normalise()
            .value());
    }
    matrix() {
        const v = this._v.value();
        const [w, x, y, z] = [this._w, v[0], v[1], v[2]];
        // prettier-ignore
        const matrix = [
            w * w + x * x - y * y - z * z, 2 * (x * y - w * z), 2 * (x * z + w * y),
            2 * (x * y + w * z), w * w - x * x + y * y - z * z, 2 * (y * z - w * x),
            2 * (x * z - w * y), 2 * (y * z + w * x), w * w - x * x - y * y + z * z
        ];
        return new SquareMatrix3D(matrix);
    }
    inverse() {
        return new Orientation3D(this._w, this._v.neg());
    }
}
//# sourceMappingURL=index.js.map